import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { ChangeOptions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { bindActionCreators } from "redux";
import { Modal, NotificationAlert } from "src/components/common";
import InlineLoading from "src/components/common/InlineLoading/InlineLoading";
import { NumberFormInput } from "src/components/form";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { ModalSize } from "src/enumerations/ModalSize";
import { TrialAccrualsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { TrialSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as AccrualANZActions from "../../../actions/accrualANZ";
import * as NavigationActions from "../../../actions/navigation";
import * as ProjectedAccrualActions from "../../../actions/projectedAccrual";
import * as TrialActions from "../../../actions/trial";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { ITmdState } from "../../../reducers/index";
import { TrialLayout } from "../../index";

import "./TrialAccrualsPage.scss";

const monthMapping = {
    january: "01",
    february: "02",
    march: "03",
    april: "04",
    may: "05",
    june: "06",
    july: "07",
    august: "08",
    september: "09",
    october: "10",
    november: "11",
    december: "12",
};

interface ChartDataObject {
    date: string;
    actual: number;
    projected: number;
}

interface TrialAccrualsPageProps {
    trial: Dtos.Trial;
    loadingTrial: boolean;
    loadTrialSuccess: boolean;
    loadTrialFailure: boolean;
    loadTrialErrorCode: string;
    savingTrial: boolean;
    saveTrialSuccess: boolean;
    saveTrialFailure: boolean;
    projectedAccruals: Dtos.ProjectedAccrual[];
    loadingProjectedAccruals: boolean;
    loadProjectedAccrualsSuccess: boolean;
    loadProjectedAccrualsFailure: boolean;
    loadProjectedAccrualsErrorCode: string;
    accrualANZ: Dtos.AccrualANZ[];
    loadingAccrualANZ: boolean;
    loadAccrualANZSuccess: boolean;
    loadAccrualANZFailure: boolean;
    loadAccrualANZErrorCode: string;
    updatingProjectedAccruals: boolean;
    updateProjectedAccrualsSuccess: boolean;
    updateProjectedAccrualsFailure: boolean;
    permissions?: Dtos.PersonnelTmdPermissions[];
    loadTrial: TrialActions.ITrialLoadByIdActionCreator;
    clearTrial: TrialActions.ITrialClearActionCreator;
    saveTrial: TrialActions.ITrialSaveActionCreator;
    loadProjectedAccruals: ProjectedAccrualActions.IProjectedAccrualLoadByTrialIdActionCreator;
    clearProjectedAccrual: ProjectedAccrualActions.IProjectedAccrualClearActionCreator;
    updateProjectAccrual: ProjectedAccrualActions.IProjectedAccrualUpdateActionCreator;
    loadAccrualANZ: AccrualANZActions.IAccrualANZLoadByTrialIdActionCreator;
    clearAccrualANZ: AccrualANZActions.IAccrualANZClearActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialAccrualsPage: FC<TrialAccrualsPageProps> = ({
    trial,
    loadingTrial,
    loadTrialSuccess,
    loadTrialFailure,
    loadTrialErrorCode,
    savingTrial,
    saveTrialSuccess,
    projectedAccruals,
    loadingProjectedAccruals,
    loadProjectedAccrualsSuccess,
    loadProjectedAccrualsFailure,
    loadProjectedAccrualsErrorCode,
    accrualANZ,
    loadingAccrualANZ,
    loadAccrualANZSuccess,
    loadAccrualANZFailure,
    loadAccrualANZErrorCode,
    permissions,
    loadTrial,
    clearTrial,
    saveTrial,
    loadProjectedAccruals,
    clearProjectedAccrual,
    updateProjectAccrual,
    loadAccrualANZ,
    clearAccrualANZ,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const trialIdNumber = parseInt(trialId, 10);

    //
    // -- States --
    const [refresh, setRefresh] = useState<boolean>(true);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [chartData, setChartData] = useState<ChartDataObject[] | undefined>(
        undefined
    );
    const [actualAccrualFormatted, setActualAccrualFormatted] = useState<
        { date: string; value: any }[] | undefined
    >(undefined);
    const [numComponents, setNumComponents] = useState<number | undefined>(
        undefined
    );
    const [inputValues, setInputValues] = useState<number[]>([]);
    const [totalAccrualInput, setTotalAccrualInput] = useState<
        number | undefined
    >(undefined);
    const [bctAccrualInput, setBCTAccrualInput] = useState<number | undefined>(
        undefined
    );
    const [internationalAccrualInput, setInternationalAccrualInput] = useState<
        number | undefined
    >(undefined);
    const [lastChanged, setLastChanged] = useState<number | undefined>(
        undefined
    );

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const loading =
        loadingTrial || loadingProjectedAccruals || loadingAccrualANZ;
    const showAccrual =
        !loadingAccrualANZ &&
        loadAccrualANZSuccess &&
        !loadingProjectedAccruals &&
        loadProjectedAccrualsSuccess &&
        !loadingTrial &&
        loadTrialSuccess;


    // Load Accrual
    useEffect(() => {
        if (trialIdNumber && refresh) {
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));
            setBreadcrumbs(TrialAccrualsBreadcrumbs(trialIdNumber, null));
            loadTrial(trialIdNumber);
            loadProjectedAccruals(trialIdNumber);
            loadAccrualANZ(trialIdNumber);
            setRefresh(false);
        }
    }, [
        loadProjectedAccruals,
        loadAccrualANZ,
        setDrawerItems,
        TrialSubMenu,
        setBreadcrumbs,
        trialIdNumber,
        refresh,
        setRefresh,
        permissions,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialFailure) {
            navigate(`/error/${loadTrialErrorCode}`);
        }
        if (loadProjectedAccrualsFailure) {
            navigate(`/error/${loadProjectedAccrualsErrorCode}`);
        }
        if (loadAccrualANZFailure) {
            navigate(`/error/${loadAccrualANZErrorCode}`);
        }
    }, [
        loadTrialFailure,
        loadProjectedAccrualsFailure,
        loadAccrualANZFailure,
        loadTrialErrorCode,
        loadProjectedAccrualsErrorCode,
        loadAccrualANZErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialIdNumber && trial?.displayName) {
            setBreadcrumbs(
                TrialAccrualsBreadcrumbs(trialIdNumber, trial?.displayName)
            );
        }
    }, [trialIdNumber, trial, setBreadcrumbs, TrialAccrualsBreadcrumbs]);

    // Format Chart Data
    useEffect(() => {
        if (projectedAccruals && actualAccrualFormatted) {
            // Create a map for quick lookup of values by date
            const projected = new Map(
                projectedAccruals.map((item) => [
                    item.date.substring(0, 7),
                    item.value,
                ])
            );
            const actual = new Map(
                actualAccrualFormatted.map((item) => [
                    item.date.substring(0, 7),
                    item.value,
                ])
            );

            // Combine all unique dates from both lists
            const allDates = Array.from(
                new Set([...projected.keys(), ...actual.keys()])
            );

            // Merged lists
            var mergedList = allDates.map((date) => ({
                date: convertDate(date),
                projected: projected.get(date) || 0, // Default to 0 if the date is missing
                actual: actual.get(date) || 0, // Default to 0 if the date is missing
            }));

            // Make date-values accumulative
            for (let index = 0; index < mergedList.length; index++) {
                if (index === 0) continue;

                mergedList[index].projected =
                    mergedList[index].projected +
                    mergedList[index - 1].projected;
                mergedList[index].actual =
                    mergedList[index].actual + mergedList[index - 1].actual;
            }

            setChartData(mergedList);
        }
    }, [projectedAccruals, actualAccrualFormatted, setChartData]);

    // Format AccrualANZ to Projected Accruals format
    useEffect(() => {
        if (accrualANZ && trial) {
            // Convert data into the { date, value } format
            var flatData = accrualANZ?.flatMap((obj) => {
                return Object.keys(monthMapping).map((month) => ({
                    date: `${obj.year}-${monthMapping[month]}-01 00:00:00.0000000 +10:00`,
                    value: obj[month],
                }));
            });

            // Remove any dates before the trial.dateActivated
            flatData = flatData?.filter(
                (x) => Date.parse(x.date) >= Date.parse(trial?.dateActivated)
            );

            // Remove trailing zeros
            var lastIndex = -1;
            for (let i = flatData.length - 1; i >= 0; i--) {
                if (flatData[i].value > 0) {
                    lastIndex = i + 1;
                    break;
                }
            }
            flatData =
                lastIndex !== -1 ? flatData.slice(0, lastIndex) : flatData;

            // Group by date and sum the values
            const groupedData = flatData?.reduce((acc, curr) => {
                if (!acc[curr.date]) {
                    acc[curr.date] = 0;
                }

                acc[curr.date] += curr.value;
                return acc;
            }, {});

            //Convert the grouped data back into an array format
            const result = Object.entries(groupedData).map(([date, value]) => ({
                date,
                value,
            }));
            setActualAccrualFormatted(result);
        }
    }, [accrualANZ, trial, setActualAccrualFormatted]);

    // Pre-fill project model values.
    useEffect(() => {
        if (projectedAccruals) {
            setNumComponents(projectedAccruals.length);
            var temp = Array(projectedAccruals.length).fill(0);
            for (let index = 0; index < projectedAccruals.length; index++) {
                temp[index] = projectedAccruals[index].value;
            }
            setInputValues(temp);
        }
    }, [setNumComponents, setInputValues, projectedAccruals]);

    useEffect(() => {
        if (trial && actualAccrualFormatted) {
            const total = actualAccrualFormatted.reduce(
                (sum, item) => sum + (item.value as number),
                0
            );
            setTotalAccrualInput(
                total + (trial.internationalTotalAccrual ?? 0)
            );
            setBCTAccrualInput(total);
            setInternationalAccrualInput(trial.internationalTotalAccrual ?? 0);
        }
    }, [
        trial,
        actualAccrualFormatted,
        setTotalAccrualInput,
        setBCTAccrualInput,
        setInternationalAccrualInput,
    ]);

    // Reload trial after saving trial group
    useEffect(() => {
        if (!savingTrial && saveTrialSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial accrual saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );
            loadTrial(trialIdNumber);
        }
    }, [savingTrial, saveTrialSuccess, loadTrial, trialIdNumber]);

    // Cleanup effect on component unmount
    useEffect(() => {
        return () => {
            clearTrial();
            clearProjectedAccrual();
            clearAccrualANZ();
        };
    }, [clearTrial, clearProjectedAccrual, clearAccrualANZ]);

    const handleChange = (event) => {
        setLastChanged(null);
        const value = parseInt(event.target.value, 10);
        if (!isNaN(value) && value >= 0) {
            setNumComponents(value);
            var maxLength =
                value < projectedAccruals?.length
                    ? value
                    : projectedAccruals.length;
            var temp = Array(value).fill(0);
            for (let index = 0; index < maxLength; index++) {
                temp[index] = projectedAccruals[index].value;
            }
            setInputValues(temp);
        } else {
            setNumComponents(0);
            setInputValues([]);
        }
    };

    const handleInputChange = (index, value) => {
        setInputValues((prevValues) => {
            const newValues = [...prevValues];
            newValues[index] = value;
            return newValues;
        });
    };

    const handleSubmit = async () => {
        var output: Dtos.ProjectedAccrual[] = [];
        for (let i = 0; i < inputValues.length; i++) {
            const newDate = new Date(trial.dateActivated);
            newDate.setMonth(newDate.getMonth() + i);
            output.push(
                new Dtos.ProjectedAccrual({
                    trialId: trialIdNumber,
                    date: newDate
                        .toISOString()
                        .replace(/\.\d{3}Z$/, ".0000000Z"),
                    value: inputValues[i],
                })
            );
        }

        await updateProjectAccrual(trialIdNumber, output);

        handleClose();
    };

    const handleClose = () => {
        setIsOpen(false);
        setNumComponents(0);
        setInputValues([]);
        loadProjectedAccruals(trialIdNumber);
        loadAccrualANZ(trialIdNumber);
    };

    const GeneratedComponent = ({
        index,
        trial,
        value,
        onChange,
        lastChanged,
        setLastChanged,
    }) => {
        const inputRef = useRef(null);
        const newDate = new Date(trial.dateActivated);
        newDate?.setMonth(newDate.getMonth() + index);
        let year = new Intl.DateTimeFormat("en", { year: "numeric" }).format(
            newDate
        );
        let month = new Intl.DateTimeFormat("en", { month: "short" }).format(
            newDate
        );

        const handleInputChange = (event) => {
            const value = parseInt(event.target.value, 10) || 0;
            onChange(index, value);
            setLastChanged(index);
        };

        useEffect(() => {
            if (lastChanged == index && inputRef.current) {
                inputRef.current.focus();
            }
        }, [value]);

        return (
            <div className="form-row py-1">
                <div className="col-5">
                    <span>
                        {month} {year}
                    </span>
                </div>
                <div className="col-7">
                    <input
                        ref={inputRef}
                        id={`numberInput-${index}`}
                        className="w-100 my-input"
                        type="number"
                        min="0"
                        placeholder="Number accrued"
                        value={value}
                        onChange={handleInputChange}
                    />
                </div>
            </div>
        );
    };

    // Input -> 2024-01 | Output -> Jan 2024
    const convertDate = (input) => {
        const [year, month] = input.split("-");

        const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ];

        const formattedMonth = monthNames[parseInt(month, 10) - 1];
        return `${formattedMonth} ${year}`;
    };

    const submitTotalAccrual = async () => {
        if (trial && internationalAccrualInput > -1) {
            let changes: Dtos.Trial = {
                internationalTotalAccrual: internationalAccrualInput,
            } as Dtos.Trial;
            await saveTrial(Object.assign({ ...trial }, changes));
        } else {
            Alert.error(
                <NotificationAlert
                    alertContent={<span>An error occurred saving the trial accrual</span>}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    };

    const renderProjectedAccrualModal = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasAnyPermission(
            permissions,
            [Dtos.Permission.TrialAccrualUpdate]
        );

        if (hasPermission) {
            return (
                <Modal
                    open={isOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={handleClose}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h1 className="mb-0">Projected Accruals</h1>
                            <span className="small text-muted">
                                Enter the number of months to project and the
                                accrual target for each month
                            </span>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={handleClose}
                        >
                            <FontAwesomeIcon
                                fixedWidth
                                icon={FontAwesomeIcons.Regular.TIMES}
                            />
                        </button>
                    </div>
                    <div className="modal-body">
                        <div>
                            <div className="form-row py-1">
                                <div className="col-5">
                                    <h6>Months to project</h6>
                                </div>
                                <div className="col-7">
                                    <input
                                        type="number"
                                        className="w-100 my-input"
                                        min="0"
                                        value={numComponents || ""}
                                        placeholder="Enter a number"
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <hr />
                            <div>
                                {Array.from({ length: numComponents }).map(
                                    (_, index) => (
                                        <GeneratedComponent
                                            key={`GeneratedComponent-${index}`}
                                            index={index}
                                            trial={trial}
                                            value={inputValues[index] || ""}
                                            onChange={handleInputChange}
                                            lastChanged={lastChanged}
                                            setLastChanged={setLastChanged}
                                        />
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                        >
                            Submit
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={handleClose}
                        >
                            Cancel
                        </button>
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        permissions,
        isOpen,
        setIsOpen,
        loadProjectedAccruals,
        trialIdNumber,
        trial,
        numComponents,
        handleChange,
        inputValues,
        handleInputChange,
        handleClose,
        handleSubmit,
    ]);

    const calculatePercentage = (numerator: number, denominator: number) => {
        if (numerator === 0) return 0;
        if (denominator === 0) return 100;

        return ((numerator / denominator) * 100).toFixed(0);
    };

    const renderTotalAccrual = useMemo(() => {
        return (
            <div className="card card-shadow p-3 my-4">
                <div className="form-row d-flex flex-direction-row align-items-center justify-content-center">
                    <span className="px-1 label">Total Accrual</span>
                    <div className="px-1">
                        <NumberFormInput
                            id={"totalaccrualinput-id"}
                            name={"totalaccrualinput-name"}
                            value={totalAccrualInput}
                            disabled={true}
                            onChange={null}
                        />
                    </div>
                    <span className="px-1 label">= Breast Cancer Trials</span>
                    <div className="px-1">
                        <NumberFormInput
                            id={"bctaccrualinput-id"}
                            name={"bctaccrualinput-name"}
                            value={bctAccrualInput}
                            disabled={true}
                            onChange={null}
                        />
                    </div>
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ minWidth: "55px" }}
                    >
                        <span className="label">
                            (
                            {calculatePercentage(
                                bctAccrualInput,
                                totalAccrualInput
                            )}
                            %)
                        </span>
                    </div>
                    <span className="pr-1 label"> - International</span>
                    <div className="px-1">
                        <NumberFormInput
                            id={"internationalaccrualinput-id"}
                            name={"internationalaccrualinput-name"}
                            value={internationalAccrualInput}
                            onChange={(
                                value: any,
                                _options?: ChangeOptions
                            ) => {
                                const validValue = Math.max(value, 0);
                                setInternationalAccrualInput(validValue);
                                setTotalAccrualInput(
                                    validValue + bctAccrualInput
                                );
                                return null;
                            }}
                        />
                    </div>
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ minWidth: "55px" }}
                    >
                        <span className="label">
                            (
                            {calculatePercentage(
                                internationalAccrualInput,
                                totalAccrualInput
                            )}
                            %)
                        </span>
                    </div>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm ml-4"
                        onClick={() => submitTotalAccrual()}
                        disabled={savingTrial}
                    >
                        Submit
                    </button>
                </div>
            </div>
        );
    }, [
        totalAccrualInput,
        setTotalAccrualInput,
        bctAccrualInput,
        setBCTAccrualInput,
        internationalAccrualInput,
        setInternationalAccrualInput,
        savingTrial,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="form-row pb-4">
                    <div className="col">
                        <h1>Trial Accruals</h1>
                    </div>
                    <div className="col d-flex justify-content-end">
                        {AuthenticationHelper.hasAnyPermission(permissions, [
                            Dtos.Permission.TrialAccrualUpdate,
                        ]) && (
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => setIsOpen(true)}
                                disabled={!trial?.dateActivated}
                            >
                                Projected Accrual
                            </button>
                        )}
                    </div>
                </div>
                {loading ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showAccrual && !loading ? (
                    <>
                        {chartData?.length > 0 ? (
                            <div className="card card-shadow p-3">
                                <div style={{ width: "100%", height: "400px" }}>
                                    <ResponsiveContainer>
                                        <AreaChart
                                            width={500}
                                            height={400}
                                            data={chartData}
                                            margin={{
                                                top: 20,
                                                right: 40,
                                                left: 0,
                                                bottom: 40,
                                            }}
                                            accessibilityLayer
                                        >
                                            <defs>
                                                <linearGradient
                                                    id="actual"
                                                    x1="0"
                                                    y1="0"
                                                    x2="0"
                                                    y2="1"
                                                >
                                                    <stop
                                                        offset="1%"
                                                        stopColor="#e8078c"
                                                        stopOpacity={0.75}
                                                    />
                                                    <stop
                                                        offset="100%"
                                                        stopColor="#e8078c"
                                                        stopOpacity={0.075}
                                                    />
                                                </linearGradient>
                                                <linearGradient
                                                    id="projected"
                                                    x1="0"
                                                    y1="0"
                                                    x2="0"
                                                    y2="1"
                                                >
                                                    <stop
                                                        offset="1%"
                                                        stopColor="#818589"
                                                        stopOpacity={0.75}
                                                    />
                                                    <stop
                                                        offset="100%"
                                                        stopColor="#818589"
                                                        stopOpacity={0.075}
                                                    />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="date"
                                                angle={45}
                                                dx={15}
                                                dy={20}
                                                axisLine={true}
                                            />
                                            <YAxis />
                                            <Tooltip />
                                            <Area
                                                name="Actual"
                                                type="monotone"
                                                dataKey="actual"
                                                stackId="1"
                                                stroke="#e8078c"
                                                fill="url(#actual)"
                                                strokeOpacity={1}
                                                fillOpacity={1}
                                            />
                                            <Area
                                                name="Projected"
                                                type="monotone"
                                                dataKey="projected"
                                                stackId="2"
                                                stroke="#818589"
                                                fill="url(#projected)"
                                                strokeOpacity={1}
                                                fillOpacity={1}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>This trial has no accrual data.</span>
                                </div>
                            </div>
                        )}
                        {trial?.internationalTrialStatusId !== 5 &&
                            renderTotalAccrual}
                    </>
                ) : null}
            </div>
        );
    }, [loading, showAccrual, chartData, trial, renderTotalAccrual]);

    return (
        <TrialLayout
            loading={loading}
            permission={Dtos.Permission.TrialAccrualView}
        >
            {renderContent}
            {renderProjectedAccrualModal}
        </TrialLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialAccrualsPageProps> => {
    let trial: Dtos.Trial | undefined = !(
        state.trials.formData instanceof Array
    )
        ? state.trials.formData
        : undefined;
    let projectedAccruals: Dtos.ProjectedAccrual[] | undefined =
        state.projectedAccrual.data instanceof Array
            ? state.projectedAccrual.data
            : undefined;
    let accrualANZ: Dtos.AccrualANZ[] | undefined =
        state.accrualANZ.data instanceof Array
            ? state.accrualANZ.data
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trial: trial!,
        loadingTrial:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Pending,
        loadTrialSuccess:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Success,
        loadTrialFailure:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Failure,
        loadTrialErrorCode: state.trials.loadState.errorCode,
        savingTrial:
            state.trials.saveState &&
            state.trials.saveState.status == RequestState.Pending,
        saveTrialSuccess:
            state.trials.saveState &&
            state.trials.saveState.status == RequestState.Success,
        saveTrialFailure:
            state.trials.saveState &&
            state.trials.saveState.status == RequestState.Failure,
        projectedAccruals: projectedAccruals!,
        loadingProjectedAccruals:
            state.projectedAccrual.loadState &&
            state.projectedAccrual.loadState.status == RequestState.Pending,
        loadProjectedAccrualsSuccess:
            state.projectedAccrual.loadState &&
            state.projectedAccrual.loadState.status == RequestState.Success,
        loadProjectedAccrualsFailure:
            state.projectedAccrual.loadState &&
            state.projectedAccrual.loadState.status == RequestState.Failure,
        loadProjectedAccrualsErrorCode:
            state.projectedAccrual.loadState.errorCode,

        updatingProjectedAccruals:
            state.projectedAccrual.updateState &&
            state.projectedAccrual.updateState.status == RequestState.Pending,
        updateProjectedAccrualsSuccess:
            state.projectedAccrual.updateState &&
            state.projectedAccrual.updateState.status == RequestState.Success,
        updateProjectedAccrualsFailure:
            state.projectedAccrual.updateState &&
            state.projectedAccrual.updateState.status == RequestState.Failure,

        accrualANZ: accrualANZ!,
        loadingAccrualANZ:
            state.accrualANZ.loadState &&
            state.accrualANZ.loadState.status == RequestState.Pending,
        loadAccrualANZSuccess:
            state.accrualANZ.loadState &&
            state.accrualANZ.loadState.status == RequestState.Success,
        loadAccrualANZFailure:
            state.accrualANZ.loadState &&
            state.accrualANZ.loadState.status == RequestState.Failure,
        loadAccrualANZErrorCode: state.accrualANZ.loadState.errorCode,

        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialAccrualsPageProps> => {
    return {
        loadTrial: bindActionCreators(TrialActions.LoadTrialById, dispatch),
        clearTrial: bindActionCreators(TrialActions.Clear, dispatch),
        saveTrial: bindActionCreators(TrialActions.SaveTrial, dispatch),
        loadProjectedAccruals: bindActionCreators(
            ProjectedAccrualActions.LoadProjectedAccrualByTrialId,
            dispatch
        ),
        clearProjectedAccrual: bindActionCreators(
            ProjectedAccrualActions.Clear,
            dispatch
        ),
        updateProjectAccrual: bindActionCreators(
            ProjectedAccrualActions.UpdateProjectedAccrual,
            dispatch
        ),
        loadAccrualANZ: bindActionCreators(
            AccrualANZActions.LoadAccrualANZByTrialId,
            dispatch
        ),
        clearAccrualANZ: bindActionCreators(AccrualANZActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialAccrualsPage);
